import { PropsWithChildren, useCallback, useEffect, useRef } from "react";
import { useAnimate, useReducedMotion } from "framer-motion";

export const SHAKE_DURATION_S = 0.4;
export const SHAKE_OFFSETS_PX = [0, -8, 8, -6, 6, -3, 3, 0];
export const ERROR_VIBRATION_PATTERN_MS = [60, 40, 60];

export const vibrateOnError = () => {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    navigator.vibrate(ERROR_VIBRATION_PATTERN_MS);
  }
};

export const useShake = () => {
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const reduceMotion = useReducedMotion();

  const shake = useCallback(() => {
    if (!reduceMotion && scope.current) {
      animate(scope.current, { x: SHAKE_OFFSETS_PX }, { duration: SHAKE_DURATION_S, ease: "easeInOut" });
    }
  }, [reduceMotion, animate, scope]);

  return [scope, shake] as const;
};

type ErrorShakeProps = PropsWithChildren<{
  hasError: boolean;
  failedSubmitCount: number;
}>;

export const ErrorShake = ({ hasError, failedSubmitCount, children }: ErrorShakeProps) => {
  const [scope, shake] = useShake();
  const handledSubmitCount = useRef(failedSubmitCount);
  const hasErrorRef = useRef(hasError);

  hasErrorRef.current = hasError;

  useEffect(() => {
    if (failedSubmitCount === handledSubmitCount.current) {
      return;
    }
    handledSubmitCount.current = failedSubmitCount;

    if (hasErrorRef.current) {
      shake();
    }
  }, [failedSubmitCount, shake]);

  return <div ref={scope}>{children}</div>;
};
